// Module: UrlInputBrick
// Role: Self-contained URL input brick for flow engine.
//       Paste URL -> submit as url-kind artefact (no presign, no file upload).
//       Optional screenshot upload path uses funnel telemetry (FTR-1317).
// Uses: api/flow-client,
//       lib/uploadTelemetry:createUploadFunnel+uploadTimeoutFor,
//       lib/imageResize:ImageResizeError,
//       screens/pantene/_uploadXHR:UploadTimeoutError
// Used by: brick-registry.ts (kind='url')
//
// Pattern: PublishLinks LinkInput UX adapted for single-URL submit.
// ADR-023: no platform literals.
// L-118: hints "screenshot also needed" when checkpoint has both url + screenshot reqs.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_PHOTO_INPUT_BYTES, FLOW_PHOTO_MAX_DIM } from '../../../shared/limits';
import { resizeImageToBlob, ImageResizeError } from '../../../lib/imageResize';
import { createUploadFunnel, uploadTimeoutFor } from '../../../lib/uploadTelemetry';
import { UploadTimeoutError } from '../../../screens/pantene/_uploadXHR';
import { submitErrorText } from '../submitErrorText';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type {
  ArtefactRequirement,
  Checkpoint,
  CheckpointAttempt,
  SlotFlowRun,
} from '../../../api/flow-types';

const SUCCESS_SOFT_BG = '#EFF7E9';

function roleFor(req: ArtefactRequirement | undefined, fallback: string): string {
  return req?.role ?? fallback;
}

export interface UrlInputBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

export function UrlInputBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: UrlInputBrickProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const [url, setUrl] = useState('');
  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [screenshotPath, setScreenshotPath] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const urlRequirement = useMemo(
    () => checkpoint.artefact_requirements.find((req) => req.kind === 'url'),
    [checkpoint.artefact_requirements],
  );
  const screenshotRequirement = useMemo(
    () => checkpoint.artefact_requirements.find((req) => req.kind === 'screenshot'),
    [checkpoint.artefact_requirements],
  );

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(
    flowRun.slot_id,
    checkpoint.id,
  );

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const isValidUrl =
    url.startsWith('http://') || url.startsWith('https://');
  const screenshotRequired = Boolean(screenshotRequirement);
  const screenshotReady = !screenshotRequired || Boolean(screenshotPath);

  useEffect(
    () => () => {
      if (thumbUrl) URL.revokeObjectURL(thumbUrl);
    },
    [thumbUrl],
  );

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text.trim());
    } catch {
      // clipboard denied -- user can type manually
    }
  }, []);

  const handlePickScreenshot = useCallback(() => {
    if (!screenshotRequirement || isDone || upload.status === 'uploading') return;
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    fileInputRef.current?.click();
  }, [canUpload, isDone, screenshotRequirement, upload.status]);

  const handleScreenshotChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file || !screenshotRequirement) return;

      // BUG-1304: 50 MB input guard
      const maxSize = screenshotRequirement.max_size_bytes ?? MAX_PHOTO_INPUT_BYTES;
      if (file.size > maxSize) {
        setHintMessage('Файл слишком большой. Уменьши и попробуй снова.');
        return;
      }

      setHintMessage(null);

      // FTR-1317: upload funnel telemetry accumulator
      const funnel = createUploadFunnel(flowRun.slot_id, checkpoint.slug);
      funnel.stage('file_picked', { mime: file.type, size: file.size });

      // BUG-1304: normalize screenshot to JPEG
      let uploadFile = file;
      try {
        funnel.stage('resize_start');
        const blob = await resizeImageToBlob(file, { maxDim: FLOW_PHOTO_MAX_DIM, quality: 0.85 });
        uploadFile = new File([blob], 'upload.jpg', { type: 'image/jpeg' });
        funnel.stage('resize_done', { size: blob.size });
      } catch (resizeErr) {
        if (resizeErr instanceof ImageResizeError && resizeErr.code === 'decode_timeout') {
          funnel.stage('resize_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
          return;
        }
        funnel.stage('resize_failed', { code: resizeErr instanceof ImageResizeError ? resizeErr.code : undefined });
        console.warn('[FTR-1317] re-encode failed, uploading original:', resizeErr);
      }

      try {
        funnel.stage('presign_start');
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: screenshotRequirement.kind,
          content_md5: '',
          file_size_bytes: uploadFile.size,
        });
        funnel.stage('presign_done');

        funnel.stage('upload_start');
        await upload.start({ url: presignResult.upload_url, file: uploadFile, timeoutMs: uploadTimeoutFor(uploadFile.size) });
        funnel.stage('upload_done');

        funnel.flush('success');
        const nextThumb = URL.createObjectURL(uploadFile);
        setThumbUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return nextThumb;
        });
        setScreenshotPath(presignResult.artefact_path);
      } catch (err: unknown) {
        const isTimeout = err instanceof UploadTimeoutError || (err instanceof DOMException && err.name === 'AbortError');
        if (isTimeout) {
          funnel.stage('upload_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
        } else {
          const msg = err instanceof Error ? err.message : 'Неизвестная ошибка';
          funnel.stage('upload_error');
          funnel.flush('failed');
          setHintMessage(`Ошибка загрузки: ${msg}`);
        }
        setScreenshotPath(null);
      }
    },
    [checkpoint.slug, flowRun.slot_id, presign, screenshotRequirement, upload],
  );

  const handleSubmit = useCallback(async () => {
    if (!isValidUrl) {
      setHintMessage(
        'Вставь ссылку, начинающуюся с http:// или https://',
      );
      return;
    }
    if (!screenshotReady) {
      setHintMessage('Загрузите скриншот публикации.');
      handlePickScreenshot();
      return;
    }

    setHintMessage(null);

    try {
      const artefacts = [
        {
          kind: 'url',
          role: roleFor(urlRequirement, 'post_url'),
          value: url,
        },
        ...(screenshotRequirement && screenshotPath
          ? [
              {
                kind: screenshotRequirement.kind,
                role: roleFor(screenshotRequirement, 'post_screenshot'),
                artefact_path: screenshotPath,
              },
            ]
          : []),
      ];

      await submitCheckpoint.mutateAsync({
        artefact_path: screenshotPath,
        artefacts,
      });
      setIsDone(true);
    } catch (err: unknown) {
      setHintMessage(submitErrorText(err));
    }
  }, [
    handlePickScreenshot,
    isValidUrl,
    screenshotPath,
    screenshotReady,
    screenshotRequirement,
    submitCheckpoint,
    url,
    urlRequirement,
  ]);

  const ctaLabel = isDone
    ? 'Готово'
    : screenshotRequired && !screenshotPath
      ? 'Загрузить скриншот'
      : 'Отправить ссылку';

  return (
    <Frame>
      <MiniHeader title={checkpoint.display_name ?? flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Вставь ссылку на публикацию
          </div>
          <div
            style={{
              font: T.footnote,
              color: S.secondaryLabel,
              marginTop: 4,
            }}
          >
            {checkpoint.instructions_text ??
              'Скопируй ссылку из приложения'}
          </div>
        </div>

        <InstructionCard p={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                disabled={isDone}
                style={{
                  flex: 1,
                  background: isDone ? SUCCESS_SOFT_BG : P.card,
                  border: `1px solid ${isDone ? P.success : P.line}`,
                  borderRadius: 12,
                  padding: '10px 12px',
                  fontFamily: T.fontBody,
                  fontSize: 15,
                  color: P.ink,
                  outline: 'none',
                  WebkitAppearance: 'none',
                  boxSizing: 'border-box',
                }}
                data-testid="url-input"
              />
              {!isDone && (
                <button
                  onClick={handlePaste}
                  aria-label="Вставить ссылку"
                  style={{
                    padding: '0 14px',
                    background: P.cream,
                    border: '1px solid rgba(192,81,58,0.18)',
                    borderRadius: 12,
                    fontFamily: T.fontBody,
                    fontSize: 13,
                    fontWeight: 600,
                    color: P.terraDark,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  Вставить
                </button>
              )}
            </div>
            {isDone && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Ic name="check" size={14} color={P.success} />
                <span style={{ font: T.caption, color: P.success }}>
                  Ссылка отправлена
                </span>
              </div>
            )}
          </div>
        </InstructionCard>

        {screenshotRequirement && (
          <InstructionCard p={12}>
            <button
              type="button"
              onClick={handlePickScreenshot}
              disabled={isDone || upload.status === 'uploading' || !canUpload}
              data-testid="url-screenshot-upload-zone"
              style={{
                width: '100%',
                minHeight: thumbUrl ? 180 : 112,
                borderRadius: 16,
                border: `2px dashed ${screenshotPath ? P.success : P.terracotta}`,
                background: screenshotPath ? SUCCESS_SOFT_BG : P.terraSoft,
                padding: thumbUrl ? 0 : '18px 12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                overflow: 'hidden',
                cursor: canUpload && !isDone ? 'pointer' : 'default',
              }}
            >
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={screenshotRequirement.label ?? 'Скриншот публикации'}
                  style={{ width: '100%', height: 180, objectFit: 'contain' }}
                />
              ) : (
                <>
                  <Ic name="camera" size={28} color={P.terracotta} />
                  <div style={{ font: T.subhead, fontWeight: 600, color: S.label }}>
                    {screenshotRequirement.label ?? 'Скриншот публикации'}
                  </div>
                  <div style={{ font: T.caption, color: S.secondaryLabel, textAlign: 'center' }}>
                    {screenshotRequirement.description ?? 'Загрузите скриншот опубликованного поста.'}
                  </div>
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleScreenshotChange}
              data-testid="url-screenshot-input"
              aria-hidden="true"
            />
          </InstructionCard>
        )}

        {hintMessage && <HintBar>{hintMessage}</HintBar>}
      </div>

      <WizardCtaBar
        label={ctaLabel}
        onClick={screenshotRequired && !screenshotPath ? handlePickScreenshot : handleSubmit}
        disabled={
          isDone ||
          !isValidUrl ||
          submitCheckpoint.isPending ||
          upload.status === 'uploading' ||
          (screenshotRequired && !canUpload)
        }
      />
    </Frame>
  );
}
